import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type Props = {
  /** Absolute URL to the authenticated attachment endpoint */
  apiUrl: string;
  filename: string;
  token: string | null | undefined;
  size?: number;
};

const FALLBACK_BG = 'rgba(0,0,0,0.04)';

export default function FeedbackAttachmentThumb({ apiUrl, filename, token, size = 56 }: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const shortName = useMemo(() => {
    const name = String(filename || '');
    if (name.length <= 14) return name;
    return `${name.slice(0, 6)}…${name.slice(-6)}`;
  }, [filename]);

  useEffect(() => {
    let didCancel = false;
    let localObjectUrl: string | null = null;

    async function load() {
      if (!apiUrl || !token) return;

      try {
        setFailed(false);
        const res = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          throw new Error(`Failed to load attachment (${res.status})`);
        }

        const blob = await res.blob();
        localObjectUrl = URL.createObjectURL(blob);

        if (!didCancel) setBlobUrl(localObjectUrl);
      } catch {
        if (!didCancel) setFailed(true);
      }
    }

    load();

    return () => {
      didCancel = true;
      if (localObjectUrl) URL.revokeObjectURL(localObjectUrl);
    };
  }, [apiUrl, token]);

  const handleOpen = () => {
    if (!blobUrl) return;
    setIsPreviewOpen(true);
  };

  const handleClose = () => setIsPreviewOpen(false);

  if (!blobUrl || failed) {
    return (
      <Box
        component="button"
        type="button"
        onClick={() => {
          // If it failed, do nothing (avoid opening protected URL without auth header).
          // If token wasn't available yet, also do nothing.
        }}
        aria-label={filename}
        title={filename}
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: FALLBACK_BG,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0.5,
          cursor: 'default',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.65rem',
            color: 'text.secondary',
            lineHeight: 1.1,
            textAlign: 'center',
            wordBreak: 'break-word',
          }}
        >
          {shortName}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={handleOpen}
        aria-label={filename}
        title={filename}
        sx={{
          width: size,
          height: size,
          p: 0,
          border: 'none',
          background: 'transparent',
          borderRadius: 1,
          cursor: 'pointer',
          display: 'inline-flex',
        }}
      >
        <Box
          component="img"
          src={blobUrl}
          alt={filename}
          sx={{
            width: size,
            height: size,
            objectFit: 'cover',
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        />
      </Box>

      <Dialog
        open={isPreviewOpen}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            m: 2,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            aria-label="Close preview"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              top: -16,
              right: -16,
              backgroundColor: 'background.paper',
              border: (theme) => `1px solid ${theme.palette.divider}`,
              '&:hover': { backgroundColor: 'background.paper' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box
            component="img"
            src={blobUrl}
            alt={filename}
            sx={{
              display: 'block',
              maxWidth: 'min(92vw, 1100px)',
              maxHeight: '88vh',
              width: 'auto',
              height: 'auto',
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.paper',
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}

